import { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { buildingById } from "@zend/content";
import { useLocaleStore } from "../i18n/index.js";
import { useSession, type Account } from "../state/session.js";
import { baseApi } from "../base/api.js";
import { useBase } from "../base/state.js";
import { firstFreeSpot } from "../base/panels/BuildMenu.js";
import { useSceneStore } from "../scene/SceneHost.js";
import { navigate } from "../router.js";
import { Button } from "../ui/Button.js";
import { AssetIcon } from "../assets/Icon.js";
import { useUnits } from "../units/state.js";
import { battlesApi, workshopApi } from "../units/api.js";
import { useReducedMotion } from "../units/useReducedMotion.js";
import { dailyApi } from "../events/api.js";
import { useStore } from "../store/state.js";
import { scrollCardAbovePanel } from "./scroll.js";
import { canSkipOpening, readOpening, saveOpening, type OpeningProgress } from "./progress.js";
import { openingText } from "./text.js";
import "./opening.css";

function browserStorage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

/** Local guidance only: all game actions remain normal authenticated, authoritative APIs. */
export function Opening({ route }: { route: string }) {
  const account = useSession((state) => state.account);
  return account?.role === "player" ? (
    <AccountOpening key={account.id} account={account} route={route} />
  ) : null;
}

function AccountOpening({ account, route }: { account: Account; route: string }) {
  const locale = useLocaleStore((state) => state.locale);
  const text = openingText[locale];
  const base = useBase((state) => state.base);
  const panel = useBase((state) => state.panel);
  const mode = useBase((state) => state.mode);
  const reveal = useUnits((state) => state.reveal);
  const result = useUnits((state) => state.result);
  const selected = useUnits((state) => state.selected);
  const purchaseFx = useStore((state) => state.fx);
  const handle = useSceneStore((state) => state.handle);
  const reducedMotion = useReducedMotion();
  const [progress, setProgress] = useState<OpeningProgress | null>(() =>
    readOpening(account.id, browserStorage()),
  );
  const [name, setName] = useState<string>(text.startName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [storeSeen, setStoreSeen] = useState(false);
  const pending = useRef(false);
  const mounted = useRef(true);
  const current = () => mounted.current && useSession.getState().account?.id === account.id;
  const step = progress?.step ?? -1;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const change = useCallback(
    (next: OpeningProgress) => {
      saveOpening(account.id, next, browserStorage());
      setProgress(next);
    },
    [account.id],
  );
  const advance = useCallback(
    (from: number) => {
      setProgress((old) => {
        if (!old || old.step !== from) return old;
        const next = { ...old, step: from + 1, collapsed: false };
        saveOpening(account.id, next, browserStorage());
        return next;
      });
    },
    [account.id],
  );

  useEffect(() => {
    // A stale store from another login must not initialize this account's opening.
    if (!progress && base?.enclave.accountId === account.id)
      change({ step: base.enclave.hqLevel >= 3 ? 8 : 0, collapsed: false, skipped: false });
  }, [progress, base, account.id, change]);

  useEffect(() => {
    if (!base || base.enclave.accountId !== account.id) return;
    if (
      step === 2 &&
      base.buildings.some((b) => b.buildingId === "bld.workshop" && b.state !== "constructing")
    )
      advance(2);
    if (step === 5 && base.enclave.hqLevel >= 3) advance(5);
  }, [base, step, account.id, advance]);

  useEffect(() => {
    if (step !== 3 && step !== 4 && step !== 6) return;
    let cancelled = false;
    let timer: number | undefined;
    const check = async () => {
      try {
        const complete =
          step === 3
            ? (await workshopApi.banners()).some(
                (banner) => banner.id === "standard" && banner.pity.pulls > 0,
              )
            : step === 4
              ? (await battlesApi.list()).some(
                  (battle) => battle.won && (battle.context === "expedition" || battle.context === "pack"),
                )
              : !(await dailyApi.view()).login.claimable;
        if (cancelled) return;
        if (complete) advance(step);
        else timer = window.setTimeout(() => void check(), 3000);
      } catch {
        if (!cancelled) timer = window.setTimeout(() => void check(), 5000);
      }
    };
    void check();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [step, advance]);

  useEffect(() => {
    if (step !== 1 || route !== "/app" || !handle || panel.kind !== "none" || mode.kind !== "view") return;
    if (reducedMotion) {
      advance(1);
      return;
    }
    const position = handle.camera.position.clone();
    const target = handle.controls.target.clone();
    const enabled = handle.controls.enabled;
    const offset = position.clone().sub(target);
    const startedAt = performance.now();
    handle.controls.enabled = false;
    const off = handle.onFrame(() => {
      const p = Math.min(1, (performance.now() - startedAt) / 4500);
      const ease = p * p * (3 - 2 * p);
      const view = offset
        .clone()
        .applyAxisAngle(new THREE.Vector3(0, 1, 0), -0.45 * (1 - ease))
        .multiplyScalar(1 + 0.16 * (1 - ease));
      handle.camera.position.copy(target).add(view);
      handle.camera.lookAt(target);
      if (p === 1) {
        off();
        advance(1);
      }
    });
    return () => {
      off();
      handle.camera.position.copy(position);
      handle.controls.target.copy(target);
      handle.controls.enabled = enabled;
      handle.camera.lookAt(target);
    };
  }, [step, route, handle, reducedMotion, advance, panel.kind, mode.kind]);

  useEffect(() => {
    if (step !== 7 || route !== "/app/store/packs") return;
    // Completion is offered only after the real starter card has entered the viewport.
    let observer: IntersectionObserver | null = null;
    const timer = window.setInterval(() => {
      const card = document.querySelector<HTMLElement>('[data-testid="offer-pack.starter"]');
      if (!card) return;
      window.clearInterval(timer);
      // The panel spans the phone width: keep the price row above it instead of centring the card behind it.
      scrollCardAbovePanel(card, document.querySelector<HTMLElement>('[data-testid="tutorial"]'), reducedMotion ? "instant" : "smooth");
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry?.isIntersecting && entry.intersectionRatio >= 0.25) setStoreSeen(true);
        },
        { threshold: 0.25 },
      );
      observer.observe(card);
    }, 200);
    return () => {
      window.clearInterval(timer);
      observer?.disconnect();
    };
  }, [step, route, reducedMotion]);

  if (!progress || !base || base.enclave.accountId !== account.id || step >= 8) return null;
  if (
    route.startsWith("/app/battle/") ||
    reveal ||
    result ||
    selected ||
    purchaseFx ||
    (route === "/app" && (panel.kind !== "none" || mode.kind !== "view"))
  )
    return null;
  const compact = progress.collapsed || (route !== "/app" && !(step === 7 && route === "/app/store/packs"));
  if (compact)
    return (
      <button
        type="button"
        className="opening-resume"
        data-testid="tutorial-resume"
        onClick={() => {
          change({ ...progress, collapsed: false });
          navigate("/app");
        }}
      >
        {text.resume} · {step + 1}/8
      </button>
    );

  const workshop = base.buildings.find((b) => b.buildingId === "bld.workshop");
  const work = async (fn: () => Promise<void>) => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch {
      if (current()) setError(text.failed);
    } finally {
      pending.current = false;
      if (current()) setBusy(false);
    }
  };
  const openHq = () => {
    navigate("/app");
    useBase.getState().setPanel({ kind: "hq" });
  };
  const placeWorkshop = () => {
    const footprint = buildingById("bld.workshop").footprint;
    if (footprint === "ring") return;
    const spot = firstFreeSpot(base, footprint.w, footprint.h);
    if (!spot) {
      useBase.getState().setPanel({ kind: "build" });
      return;
    }
    useBase
      .getState()
      .setMode({ kind: "place", buildingId: "bld.workshop", w: footprint.w, h: footprint.h, rot: 0 });
    useBase.getState().setGhost({ ...spot, valid: true });
  };
  return (
    <aside className="opening-panel" data-testid="tutorial" data-step={step} aria-label={text.title}>
      <div className="opening-heading">
        <span>
          {text.step} {step + 1}/8
        </span>
        <button
          type="button"
          onClick={() => change({ ...progress, collapsed: true })}
          aria-label={text.minimize}
        >
          −
        </button>
      </div>
      <div className="opening-title">
        <AssetIcon
          id={
            step === 3 || step === 4
              ? "unit.shieldwall_mk2"
              : step === 6 || step === 7
                ? "itm.chest_gold"
                : "bld.command_nexus"
          }
          size={54}
        />
        <h2>{text.headings[step]}</h2>
      </div>
      <p>{text.details[step]}</p>
      {step === 0 ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void work(async () => {
              await baseApi.rename(name);
              if (!current()) return;
              await useBase.getState().refresh();
              if (current()) advance(0);
            });
          }}
        >
          <label htmlFor="enclave-name">{text.name}</label>
          <input
            id="enclave-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            minLength={3}
            maxLength={32}
            required
          />
          <Button type="submit" variant="primary" disabled={busy} data-testid="tutorial-action">
            {text.save}
          </Button>
        </form>
      ) : step === 1 ? (
        <p role="status">{text.intro}</p>
      ) : (
        <Button
          variant="primary"
          disabled={busy || (step === 2 && workshop?.state === "constructing")}
          data-testid="tutorial-action"
          onClick={() => {
            if (step === 2) {
              if (base.enclave.hqLevel < 2) openHq();
              else placeWorkshop();
            }
            if (step === 3) navigate("/app/units/workshop");
            if (step === 4) navigate("/app/expeditions");
            if (step === 5) openHq();
            if (step === 6) navigate("/app/events/daily");
            if (step === 7) {
              if (storeSeen) advance(7);
              else navigate("/app/store/packs");
            }
          }}
        >
          {step === 2
            ? workshop
              ? text.wait
              : base.enclave.hqLevel < 2
                ? text.hq
                : text.build
            : step === 3
              ? text.pull
              : step === 4
                ? text.fight
                : step === 5
                  ? text.hq
                  : step === 6
                    ? text.daily
                    : storeSeen
                      ? text.finish
                      : text.store}
        </Button>
      )}
      {error ? (
        <p role="alert" className="opening-error">
          {error}
        </p>
      ) : null}
      {canSkipOpening(progress) && !(step === 7 && storeSeen) ? (
        <button
          type="button"
          className="opening-skip"
          data-testid="tutorial-skip"
          onClick={() => change({ step: 8, skipped: true, collapsed: false })}
        >
          {text.skip}
        </button>
      ) : null}
    </aside>
  );
}
